/**
 * VSCodeExtensionSetupWizard Component
 *
 * Setup wizard for installing the LeftShield VS Code extension.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  tokens,
  Divider,
  MessageBar,
  MessageBarBody,
  Link,
} from '@fluentui/react-components';
import {
  CopyRegular,
  CheckmarkRegular,
  SettingsRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: '600px',
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  codeBlock: {
    position: 'relative',
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
  copyButton: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    right: tokens.spacingHorizontalXS,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalL,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  settingsJson: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
  },
  installButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: tokens.borderRadiusMedium,
    textDecoration: 'none',
    fontWeight: tokens.fontWeightSemibold,
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
});

interface VSCodeExtensionSetupWizardProps {
  open: boolean;
  onClose: () => void;
  apiKey?: string;
}

export const VSCodeExtensionSetupWizard: React.FC<VSCodeExtensionSetupWizardProps> = ({
  open,
  onClose,
  apiKey = 'YOUR_API_KEY',
}) => {
  const styles = useStyles();
  const [copiedStep, setCopiedStep] = useState<number | null>(null);

  const copyToClipboard = (text: string, step: number) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const settingsJson = `{
  "leftshield.apiKey": "${apiKey}",
  "leftshield.validateOnSave": true,
  "leftshield.validateOnOpen": false,
  "leftshield.severity.minimum": "medium",
  "leftshield.exclude": [
    "**/node_modules/**",
    "**/dist/**",
    "**/*.min.js"
  ]
}`;

  const cliInstall = 'code --install-extension leftshield.leftshield-vscode';

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Set Up VS Code Extension</DialogTitle>
          <DialogContent className={styles.content}>
            <MessageBar intent="info">
              <MessageBarBody>
                The LeftShield VS Code extension provides real-time code validation with inline problem markers and quick fixes.
              </MessageBarBody>
            </MessageBar>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>1</span>
                <Text weight="semibold">Install Extension</Text>
              </div>
              <Text size={200}>
                Install from the VS Code Marketplace or via command line:
              </Text>
              <Link
                href="vscode:extension/leftshield.leftshield-vscode"
                className={styles.installButton}
              >
                Open in VS Code
              </Link>
              <Text size={200}>Or via command line:</Text>
              <div className={styles.codeBlock}>
                <code>{cliInstall}</code>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 1 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(cliInstall, 1)}
                />
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>2</span>
                <Text weight="semibold">Configure Extension</Text>
              </div>
              <Text size={200}>
                Open VS Code settings (<code>Ctrl+,</code>) and add your API key, or add to <code>settings.json</code>:
              </Text>
              <div className={styles.codeBlock}>
                <pre style={{ margin: 0 }}>{settingsJson}</pre>
                <Button
                  className={styles.copyButton}
                  appearance="subtle"
                  size="small"
                  icon={copiedStep === 2 ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={() => copyToClipboard(settingsJson, 2)}
                />
              </div>
            </div>

            <Divider />

            <div className={styles.step}>
              <div className={styles.stepHeader}>
                <SettingsRegular />
                <Text weight="semibold">Features</Text>
              </div>
              <div className={styles.featureList}>
                <div className={styles.featureItem}>
                  <CheckmarkRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                  <Text size={200}>Real-time validation on file save</Text>
                </div>
                <div className={styles.featureItem}>
                  <CheckmarkRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                  <Text size={200}>Inline problem markers (squiggles)</Text>
                </div>
                <div className={styles.featureItem}>
                  <CheckmarkRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                  <Text size={200}>Quick fix suggestions</Text>
                </div>
                <div className={styles.featureItem}>
                  <CheckmarkRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                  <Text size={200}>Status bar indicator</Text>
                </div>
                <div className={styles.featureItem}>
                  <CheckmarkRegular style={{ color: tokens.colorPaletteGreenForeground1 }} />
                  <Text size={200}>Manual validation command</Text>
                </div>
              </div>
            </div>

            <MessageBar intent="success">
              <MessageBarBody>
                The extension will automatically validate your code as you work.{' '}
                <Link href="https://docs.leftshield.io/vscode-extension" target="_blank">
                  View documentation
                </Link>
              </MessageBarBody>
            </MessageBar>
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
